<!-- The panel element lives in the rail; on expand it is MOVED (DomPortal) into
     a body-level CDK overlay (see openExpand) to escape the sticky preview
     column's stacking context, then returned here on collapse. -->
<div class="preview-panel">
  <div
    #panelHost
    class="preview-panel__inner"
    [class.preview-panel__inner--expanded]="expanded()"
    [cdkTrapFocus]="expanded()"
    [cdkTrapFocusAutoCapture]="expanded()"
    [attr.role]="expanded() ? 'dialog' : null"
    [attr.aria-modal]="expanded() ? 'true' : null"
    [attr.aria-label]="expanded() ? 'Full-screen preview' : null"
  >
    <!-- ── Unified preview toolbar ─────────────────────────────────────── -->
    <div class="preview-panel__toolbar">
      <!-- Header: title + Compare/Expand on their own row (consistent outline
           buttons with icons) so they never wrap under the Surface picker. -->
      <div class="ppt-header">
        <!-- Collapse the rail from the rail itself. The hero button can hide
             the preview too, but it sits far from the thing it controls; this
             puts the control where the user is already looking. Hidden while
             expanded — there the panel is a modal overlay, and Collapse
             (below) is the correct way out. -->
        @if (!expanded()) {
        <button
          type="button"
          class="ppt-collapse"
          aria-label="Hide preview"
          title="Hide preview"
          (click)="collapse.emit()"
        >
          <svg
            class="ppt-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
        }
        <span class="ppt-title">Customer view</span>
        @if (popId()) {
        <span
          class="rounded-full px-2 py-0.5 text-xs enr-text-surface"
          title="The preview shows the price this page edits"
          data-testid="preview-pinned-tag"
        >
          Pinned to this price
        </span>
        }
        <div class="ppt-actions">
          <enr-button
            variant="outline"
            size="small"
            [leadingIcon]="true"
            [disabled]="!canCompare()"
            (clicked)="toggleCompare()"
          >
            <svg
              slot="leading-icon"
              class="ppt-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="4" width="7" height="16" rx="1.5" />
              <rect x="14" y="4" width="7" height="16" rx="1.5" />
            </svg>
            {{ compareMode() ? "Exit compare" : "Compare" }}
          </enr-button>
          <enr-button
            variant="outline"
            size="small"
            [leadingIcon]="true"
            (clicked)="toggleExpand()"
          >
            @if (expanded()) {
            <svg
              slot="leading-icon"
              class="ppt-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path
                d="M9 9 4 4m0 0v4m0-4h4M15 9l5-5m0 0v4m0-4h-4M9 15l-5 5m0 0v-4m0 4h4m11-5-5 5m0 0v-4m0 4h4"
              />
            </svg>
            } @else {
            <svg
              slot="leading-icon"
              class="ppt-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path
                d="M8 3H5a2 2 0 0 0-2 2v3m13-5h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3m13 5h3a2 2 0 0 0 2-2v-3"
              />
            </svg>
            } {{ expanded() ? "Collapse" : "Expand" }}
          </enr-button>
        </div>
      </div>

      <!-- Customer view — a single searchable dropdown of every channel with
           its full "Surface · View" label, matching the list-page quick
           preview so both toolbars read the same. -->
      <div class="ppt-group">
        <span class="ppt-label">Surface</span>
        <div class="ppt-select">
          <enr-select
            size="small"
            aria-label="Preview surface"
            [options]="channelOptions()"
            [searchable]="true"
            [value]="hostChannelId()"
            [disabled]="deviceLocked()"
            (valueChange)="selectChannel($any($event))"
          ></enr-select>
        </div>
      </div>

      @if (deviceLocked()) {
      <p class="ppt-hint">
        Device offering — always shown on the Online Store surface.
      </p>
      }

      <!-- Language — follows the editor's language picker when it targets a
           single language; independent (own switcher) only on "ALL". (#51) -->
      <div class="ppt-group">
        <span class="ppt-label">Language</span>
        @if (!compareMode()) {
        <div
          class="ppt-pills"
          role="tablist"
          aria-label="Preview language"
          [title]="
            languageFollowsForm()
              ? 'Following the editor language. Switch the editor to ALL to choose a preview language here.'
              : null
          "
        >
          @for (l of activeLanguages(); track l.locale) {
          <button
            type="button"
            class="ppt-pill"
            role="tab"
            [class.ppt-pill--active]="isLanguageAActive(l.locale)"
            [attr.aria-selected]="isLanguageAActive(l.locale)"
            [disabled]="languageFollowsForm()"
            (click)="setLanguageA(l.locale)"
          >
            {{ l.shortCode }}
          </button>
          }
        </div>
        } @else {
        <div class="ppt-compare-langs">
          <div class="ppt-pills" aria-label="Compare language A (left)">
            <span class="ppt-ab">A</span>
            @for (l of activeLanguages(); track l.locale) {
            <button
              type="button"
              class="ppt-pill"
              [class.ppt-pill--active]="isLanguageAActive(l.locale)"
              (click)="setLanguageA(l.locale)"
            >
              {{ l.shortCode }}
            </button>
            }
          </div>
          <div class="ppt-pills" aria-label="Compare language B (right)">
            <span class="ppt-ab">B</span>
            @for (l of activeLanguages(); track l.locale) {
            <button
              type="button"
              class="ppt-pill"
              [class.ppt-pill--active]="isLanguageBActive(l.locale)"
              (click)="setLanguageB(l.locale)"
            >
              {{ l.shortCode }}
            </button>
            }
          </div>
        </div>
        }
      </div>

      <!-- Active-selection summary — redundant with the controls above for
           sighted users; kept visually-hidden as a one-line summary for
           assistive tech (and the device-lock invariant test). -->
      <p class="ppt-caption">{{ caption() }}</p>
    </div>

    <!-- ── Stage(s): 1 (single/expand) or 2 (compare) chromeless panes ──── -->
    <div
      class="preview-panel__stages"
      [class.preview-panel__stages--compare]="compareMode()"
    >
      <app-enrichment-mobile-preview
        class="preview-panel__pane"
        [showChrome]="false"
        [localeOverride]="resolvedLocaleA()"
        [channelIdOverride]="hostChannelId()"
        [pinnedPopId]="popId()"
        (deviceLineChange)="onDeviceLineChange($event)"
      ></app-enrichment-mobile-preview>

      @if (compareMode()) {
      <app-enrichment-mobile-preview
        class="preview-panel__pane"
        [showChrome]="false"
        [localeOverride]="resolvedLocaleB()"
        [channelIdOverride]="hostChannelId()"
        [pinnedPopId]="popId()"
      ></app-enrichment-mobile-preview>
      }
    </div>
  </div>
</div>
